import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../Button';
import './ContentBannerV3.css';

/**
 * ContentBannerV3 — a row of equal «banner» cells, each a centred column:
 * title, a product photo in a fixed-ratio box, copy and a CTA. Two cells side
 * by side on desktop, stacked on mobile (Crown Student homepage: «Designa ditt
 * Champagneglas» / «Kolla in Studentprylar»).
 *
 * Figma (Crownstudent-MCP `HoZ60grGAGMTrlmGnOJxwV`): desktop `69:19251`
 * (1600×525: two 736px cells, content 672 wide, the photo box 3:1), mobile
 * `129:5943` (400 wide: one cell per row, the photo box 3:2). A gray-075 rule
 * separates the cells — a vertical divider between the columns on desktop, a
 * horizontal one between the stacked cells on mobile; nothing under the block.
 *
 * - `items` — one entry per cell: `title` (styled `typo-h3`; the element is
 *   `titleAs`, h2 by default), `text` (P.Large, gray-6), `image`
 *   `{ src, srcMobile?, alt }` shown `object-fit: contain` inside the box
 *   (`srcMobile` = the phone crop, when the photo needs its own), `ctaLabel`
 *   with `ctaHref` (a real link) / `ctaOnClick` / `ctaVariant` (Button
 *   secondary `main` by default). A cell without an image has no photo box; one
 *   without a label has no button.
 * - Renders nothing without items. Every value is a DS token — no component
 *   tokens. CSS hooks a host may set: `--content-banner-v3-ratio` (3 / 2) /
 *   `--content-banner-v3-ratio-desktop` (3 / 1), `--content-banner-v3-rule`
 *   (gray-075).
 */
const ContentBannerV3 = ({
  items = [],
  titleAs: Title = 'h2',
  className = '',
  ...rest
}) => {
  const cells = (items || []).filter(Boolean);
  if (cells.length === 0) return null;

  return (
    <section className={['content-banner-v3', className].filter(Boolean).join(' ')} {...rest}>
      <div className="content-banner-v3__container">
        <div className="content-banner-v3__grid">
          {cells.map((item, index) => {
            const { title, text, image, ctaLabel, ctaHref, ctaOnClick, ctaVariant = 'main' } = item;
            return (
              <article key={title || image?.src || index} className="content-banner-v3__item">
                {title && <Title className="content-banner-v3__title typo-h3">{title}</Title>}

                {image?.src && (
                  <figure className="content-banner-v3__media">
                    <picture className="content-banner-v3__picture">
                      {image.srcMobile && (
                        <source media="(max-width: 1023px)" srcSet={image.srcMobile} />
                      )}
                      <img
                        className="content-banner-v3__img"
                        src={image.src}
                        alt={image.alt || ''}
                        loading="lazy"
                      />
                    </picture>
                  </figure>
                )}

                {text && <p className="content-banner-v3__text typo-body-lg">{text}</p>}

                {ctaLabel && (
                  <div className="content-banner-v3__cta">
                    <Button size="secondary" variant={ctaVariant} href={ctaHref} onClick={ctaOnClick}>
                      {ctaLabel}
                    </Button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

ContentBannerV3.propTypes = {
  /** One cell each: { title, text, image: { src, srcMobile, alt }, ctaLabel, ctaHref, ctaOnClick, ctaVariant } */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      text: PropTypes.string,
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        srcMobile: PropTypes.string,
        alt: PropTypes.string,
      }),
      ctaLabel: PropTypes.string,
      ctaHref: PropTypes.string,
      ctaOnClick: PropTypes.func,
      ctaVariant: PropTypes.oneOf([
        'main', 'alternative', 'danger', 'outline', 'black', 'white', 'gray', 'text', 'hero',
      ]),
    }),
  ),
  /** Heading element of every cell title — h2 by default; h3 under a section's own h2 (the style is always typo-h3) */
  titleAs: PropTypes.elementType,
  /** Additional CSS class on the section (other attributes reach it too) */
  className: PropTypes.string,
};

export default ContentBannerV3;
